import * as React from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "destructive"
  | "ghost"
  | "link"
  | "brand"
  | "info"
  | "success";

export type ButtonSize = "xs" | "sm" | "md" | "lg";

type ButtonOwnProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /** variant=link only. default `true` (underline-offset-4 hover:underline). `false` → no-underline. */
  underline?: boolean;
  className?: string;
  children?: React.ReactNode;
  disabled?: boolean;
};

export type ButtonProps =
  | (ButtonOwnProps & { as?: "button" } & Omit<
      React.ButtonHTMLAttributes<HTMLButtonElement>,
      keyof ButtonOwnProps
    >)
  | (ButtonOwnProps & { as: "a" } & Omit<
      React.AnchorHTMLAttributes<HTMLAnchorElement>,
      keyof ButtonOwnProps
    >)
  | (ButtonOwnProps & { as: typeof Link } & Omit<
      React.ComponentProps<typeof Link>,
      keyof ButtonOwnProps
    >);

// whitespace-nowrap: prevent multi-word labels like "Add Domain" / "Save
// changes" from breaking into two lines on narrow viewports — a button
// should grow horizontally (and wrap with the parent flex container if
// needed), not split mid-label.
const BASE =
  "whitespace-nowrap rounded-md font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background";

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary: "bg-primary text-primary-foreground hover:bg-primary/90",
  secondary:
    "bg-card border border-border text-foreground hover:bg-muted",
  destructive:
    "bg-destructive text-destructive-foreground hover:bg-destructive/90",
  ghost: "text-foreground hover:bg-muted shadow-none",
  link: "text-info shadow-none",
  brand: "bg-foreground text-background hover:bg-foreground/90",
  info: "bg-info text-info-foreground hover:bg-info/90",
  success: "bg-success text-success-foreground hover:bg-success/90",
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  xs: "h-7 px-2 py-1 text-xs",
  sm: "h-8 px-3 text-xs",
  md: "h-9 px-3.5 text-sm",
  lg: "h-10 px-4 text-sm",
};

const LINK_UNDERLINE_ON = "underline-offset-4 hover:underline";
const LINK_UNDERLINE_OFF = "no-underline";

type ButtonRef = HTMLButtonElement | HTMLAnchorElement;

export const Button = React.forwardRef<ButtonRef, ButtonProps>(function Button(
  props,
  ref,
) {
  const {
    variant = "primary",
    size = "md",
    loading = false,
    disabled = false,
    underline,
    className,
    children,
  } = props;

  const isDisabled = disabled || loading;

  const linkExtra =
    variant === "link"
      ? underline === false
        ? LINK_UNDERLINE_OFF
        : LINK_UNDERLINE_ON
      : "";

  const classes = [
    BASE,
    VARIANT_CLASSES[variant],
    SIZE_CLASSES[size],
    linkExtra,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const loadingIcon = loading ? (
    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
  ) : null;

  // Branch by `as`. Default = button.
  // `as` may be undefined, "button", "a", or `typeof Link`.
  const as = (props as { as?: unknown }).as;

  if (as === "a") {
    const {
      as: _ignore1,
      variant: _v,
      size: _s,
      loading: _l,
      underline: _u,
      disabled: _d,
      className: _c,
      children: _ch,
      onClick,
      href,
      tabIndex,
      ...rest
    } = props as ButtonOwnProps & { as: "a" } & React.AnchorHTMLAttributes<HTMLAnchorElement>;
    const guardedOnClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (isDisabled) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      onClick?.(e);
    };
    return (
      <a
        ref={ref as React.Ref<HTMLAnchorElement>}
        className={classes}
        href={isDisabled ? undefined : href}
        aria-disabled={isDisabled || undefined}
        tabIndex={isDisabled ? -1 : tabIndex}
        onClick={guardedOnClick}
        {...rest}
      >
        {loadingIcon}
        {children}
      </a>
    );
  }

  if (as === Link) {
    const {
      as: _ignore1,
      variant: _v,
      size: _s,
      loading: _l,
      underline: _u,
      disabled: _d,
      className: _c,
      children: _ch,
      onClick,
      tabIndex,
      ...rest
    } = props as ButtonOwnProps & { as: typeof Link } & React.ComponentProps<typeof Link>;
    const guardedOnClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (isDisabled) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      onClick?.(e);
    };
    return (
      <Link
        ref={ref as React.Ref<HTMLAnchorElement>}
        className={classes}
        aria-disabled={isDisabled || undefined}
        tabIndex={isDisabled ? -1 : tabIndex}
        onClick={guardedOnClick}
        {...rest}
      >
        {loadingIcon}
        {children}
      </Link>
    );
  }

  // default: button
  const {
    as: _ignore1,
    variant: _v,
    size: _s,
    loading: _l,
    underline: _u,
    disabled: _d,
    className: _c,
    children: _ch,
    type,
    ...rest
  } = props as ButtonOwnProps & { as?: "button" } & React.ButtonHTMLAttributes<HTMLButtonElement>;
  return (
    <button
      ref={ref as React.Ref<HTMLButtonElement>}
      type={type ?? "button"}
      disabled={isDisabled}
      className={classes}
      {...rest}
    >
      {loadingIcon}
      {children}
    </button>
  );
});
